import { createTheme, PaletteOptions, ThemeOptions } from "@mui/material/styles"

import "@fontsource/figtree"
// import i18n from "i18next"
import { useTheme } from "@mui/material"
import i18n from "../utils/i18n"

interface Props {
    MuiButton: {
        size: "small" | "medium" | "large"
    }
    MuiButtonGroup: {
        size: "small" | "medium" | "large"
    }
    MuiCheckbox: {
        size: "small" | "medium"
    }
    MuiFab: {
        size: "small" | "medium" | "large"
    }
    MuiFormControl: {
        margin: "dense" | "none" | "normal"
        size: "small" | "medium"
    }
    MuiFormHelperText: {
        margin: "dense" | "none" | "normal"
    }
    MuiIconButton: {
        size: "small" | "medium"
    }
    MuiInputBase: {
        margin: "dense" | "none" | "normal"
    }
    MuiInputLabel: {
        margin: "dense" | "none" | "normal"
    }
    MuiRadio: {
        size: "small" | "medium"
    }
    MuiSwitch: {
        size: "small" | "medium"
    }
    MuiTextField: {
        margin: "dense" | "none" | "normal"
        size: "small" | "medium"
    }
}

declare module "@mui/material/styles" {
    interface Theme {
        props?: Props
    }
    interface ThemeOptions {
        props?: Props
    }
    interface PaletteOptions {
        type: "light" | "dark"
    }
}
export const colorfulGraphicsColors = {
    cyanBlue: "#4492C6",
    accentOrange: "#FF8039",
    limeGreen: "#42ba96",
    accentYellow: "#E6B10E",
}

export const graphicsColors = {
    s: "#003B5C",
    m: "#005683",
    l: "#0078BF",
    xl: "#79ABD2",
    accentBlue: "#0D9DDB",
    cyanBlueDark: "#28628E",
    ...colorfulGraphicsColors,
}

export const indexedGraphicsColors = (index: number, forceColorful = false) => {
    const colors = Object.values(forceColorful ? colorfulGraphicsColors : graphicsColors)
    return colors[index % colors.length]
}

export const deterministicRandomGraphicColor = (id: number | string) => {
    // Seed is provided optionally as number or string.
    // If not provided, a random number is generated.
    // Else, generate a deterministic random number using seed, take mod array length and select a color
    const randomIndex = Math.abs(Math.floor(parseInt(id.toString(), 36))) % Object.values(graphicsColors).length

    const colors = Object.values(graphicsColors)
    return colors[randomIndex]
}

export const stringAvatar = (name: string) => {
    if (name !== undefined && name !== null) {
        const firstLetterOfFirstName = name.split(" ") ?? ""
        let avatarLetters = firstLetterOfFirstName[0][0] ?? ""
        avatarLetters += (firstLetterOfFirstName[1] && firstLetterOfFirstName[1][0]) ?? ""
        avatarLetters = avatarLetters.toUpperCase()
        return {
            children: avatarLetters,
        }
    }
}

const verbColors = {
    error: {
        main: "#f44336",
        orange: "#FF8039",
    },
    info: {
        main: "#79ABD2",
        dark: "#0D92D6",
    },
    success: {
        main: "#388e3c",
        light: "#57ab6d",
    },
    warning: {
        main: "#f57c00",
    },
}

export const darkPalette = {
    mode: "dark",
    type: "dark",
    primary: {
        main: "#4492c6",
        dark: "#2f668a",
        contrast: "#00263A",
        orange: "#FF8039",
    },
    secondary: {
        main: "#00263a",
        dark: "#00131d",
        light: "#004367",
    },
    text: {
        primary: "#ffffff",
    },

    divider: "#00131d",
    background: {
        default: "#00263a",
        paper: "#00263a",
    },
    marketplace: {
        card: "#0d3f59",
    },
    graphics: graphicsColors,
    ...verbColors,
} as PaletteOptions

export const themeOptions: ThemeOptions = {
    direction: i18n.dir() || "ltr",
    shape: {
        borderRadius: 4,
    },
    spacing: 8,
    typography: {
        fontFamily: "Proxima Nova",
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        fontWeightBold: 700,
        h1: {
            fontSize: "85px", // Change the font size of h1
        },
        h2: {
            fontSize: "60px", // Change the font size of h2
        },
        h3: {
            fontSize: "48px", // Change the font size of h3
        },
        h4: {
            fontSize: "34px", // Change the font size of h4
        },
        h5: {
            fontSize: "24px", // Change the font size of h5
        },
        h6: {
            fontSize: "20px", // Change the font size of h6
        },
        subtitle1: {
            fontSize: "16px", // Change the font size of subtitle1
        },
        subtitle2: {
            fontSize: "14px", // Change the font size of subtitle2
        },
        body1: {
            fontSize: "16px", // Change the font size of body1
        },
        body2: {
            fontSize: "14px", // Change the font size of body2
        },
        button: {
            small: {
                fontSize: "13px", // Change the font size of button
            },
            medium: {
                fontSize: "14px", // Change the font size of button
            },
            large: {
                fontSize: "15px", // Change the font size of button
            },
        },
        caption: {
            fontSize: "12px", // Change the font size of caption
        },
        overline: {
            fontSize: "12px", // Change the font size of overline
        },
    },
    props: {
        MuiButton: {
            size: "small",
        },
        MuiButtonGroup: {
            size: "small",
        },
        MuiCheckbox: {
            size: "small",
        },
        MuiFab: {
            size: "small",
        },
        MuiFormControl: {
            margin: "dense",
            size: "small",
        },
        MuiFormHelperText: {
            margin: "dense",
        },
        MuiIconButton: {
            size: "small",
        },
        MuiInputBase: {
            margin: "dense",
        },
        MuiInputLabel: {
            margin: "dense",
        },
        MuiRadio: {
            size: "small",
        },
        MuiSwitch: {
            size: "small",
        },
        MuiTextField: {
            margin: "dense",
            size: "small",
        },
    },
}
export const theme = createTheme(themeOptions)

//custom styling for mui form to override the default theme
export const MuiFormTheme = createTheme({
    palette: {
        type: "dark",
        mode: "dark",
        primary: {
            main: "#ffffff",
        },
        text: {
            primary: "#ffffff",
            secondary: "#ffffff",
        },
    },
    typography: {
        fontSize: 16,
        fontFamily: "Proxima Nova",
        allVariants: {
            color: "#ffffff",
        },
    },
    components: {
        MuiRadio: {
            styleOverrides: {
                root: {
                    color: "#ffffff",
                    "&.Mui-checked": {
                        color: "#ffffff",
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    backgroundColor: "transparent",
                    border: "2px solid #ffffff",
                    borderRadius: "10px",
                    color: "#ffffff",
                    fontFamily: "Proxima Nova",
                    fontSize: "18px",
                    paddingY: { xs: "16px", sm: "15px", md: "14px" },
                    "&:hover": {
                        backgroundColor: "transparent",
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    color: "#ffffff",
                    fontFamily: "Proxima Nova",
                    fontSize: "18px",
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ffffff", // Outline color
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ffffff", // Outline color on hover
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ffffff", // Outline color on focus
                    },
                },
                input: {
                    color: "#ffffff",
                },
            },
        },
    },
})

export const useChartStyleOptions = () => {
    const theme = useTheme()

    const fontColor = "#000"
    return {
        hAxis: { title: "Date", titleTextStyle: { color: fontColor }, textStyle: { color: fontColor } },
        vAxis: { minValue: 0, titleTextStyle: { color: fontColor }, textStyle: { color: fontColor } },
        legend: { position: "bottom", maxLines: 3, textStyle: { color: fontColor } },

        // background color
        backgroundColor: theme.palette.background.paper,
        // Line color
        colors: ["#0099dd"],
    }
}

export const hiddenScrollbar = {
    overflow: "auto",
    "&::-webkit-scrollbar": {
        display: "none",
        width: "0px",
    },
}
